import { Router } from 'vue-router'
import { useSEO } from '@/composables/useSEO'
import { getConfig } from '@/config'
import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('metadata')

import { ResourceType } from '@/types'

/**
 * Map of route names to default titles
 * This will be used when no API data is available or during loading
 */
const routeTitles: Record<string, string> = {
  home: 'Home',
  browse: 'Browse',
  search: 'Search',
  library: 'My Library',
  settings: 'Settings',
  login: 'Sign In',
  register: 'Create Account',
  'forgot-password': 'Forgot Password',
  'reset-password': 'Reset Password',
  title: 'Content Details',
  support: 'Support'
}

/**
 * Map of route names to resource types for metadata defaults
 */
const routeResourceMap: Record<string, ResourceType> = {
  browse: 'titles', // Default, will be overridden by route params
  search: 'titles',
  title: 'titles',
  authors: 'authors',
  categories: 'categories',
  series: 'series',
  casts: 'casts'
}

/**
 * Valid resource types that can appear in URLs
 */
const validResourceTypes: ResourceType[] = [
  'titles',
  'categories',
  'authors',
  'series',
  'casts',
  'sliders'
]

/**
 * Get resource type from route
 */
const getResourceTypeFromRoute = (route: any): ResourceType | null => {
  const path = route.path
  if (path.startsWith('/titles')) return 'titles'
  if (path.startsWith('/categories')) return 'categories'
  if (path.startsWith('/authors')) return 'authors'
  if (path.startsWith('/series')) return 'series'
  if (path.startsWith('/casts')) return 'casts'
  return null
}

/**
 * Build route title
 */
const buildRouteTitle = (name?: string): string => {
  if (!name) return ''
  return name.split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const setupMetadataPlugin = (router: Router) => {
  // Set up route navigation hook to update metadata
  router.beforeEach((to, from, next) => {
    const { resetMeta, updateMeta, getResourceDefaultMeta } = useSEO()
    
    // Reset meta to default values
    resetMeta()
    
    // Check if we have a resource type in the route
    const resourceType = getResourceTypeFromRoute(to)
    
    if (resourceType) {
      // If we have a resource type, use its specific defaults
      const resourceDefaults = getResourceDefaultMeta(resourceType)
      
      // Update legacy meta system - the UISeoHead component will handle useHead
      updateMeta({
        title: resourceDefaults.title,
        description: resourceDefaults.description,
        type: resourceDefaults.type,
        url: typeof window !== 'undefined' ? window.location.origin + to.fullPath : to.fullPath
      })
    } else {
      // Otherwise use general route title
      const routeTitle = buildRouteTitle(to.name?.toString())
      
      // Update legacy meta system - the UISeoHead component will handle useHead
      updateMeta({ 
        title: routeTitle,
        url: typeof window !== 'undefined' ? window.location.origin + to.fullPath : to.fullPath
      })
    }
    
    next()
  })
  
  // Register the global metadata properties
  fileLogger.log('Initialized')
} 